import React from "react"
import { Box, Button, Card, CardContent, FormControlLabel, Grid, MenuItem, Stack, Switch, TextField, Typography } from "@mui/material"

import { ImageWidget } from "../CustomWidgets"
import { PresentationFormData } from "./wheelPrizeTypes"

// Mirrors win-facil-web-app's theme `secondary.main` (userStore.ts) — shown as the color
// picker's starting swatch when no override is set, so admins see the actual default rather
// than an arbitrary color.
const DEFAULT_FAB_BACKGROUND_COLOR = "#edf2ff"

export const WheelPresentationConfig: React.FC<{
  presentation: PresentationFormData
  onChange: (patch: Partial<PresentationFormData>) => void
}> = ({ presentation, onChange }) => (
  <Stack spacing={3}>
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          Header
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Wheel title"
              value={presentation.title}
              onChange={(event) => onChange({ title: event.target.value })}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Subtitle"
              value={presentation.subtitle}
              onChange={(event) => onChange({ subtitle: event.target.value })}
              fullWidth
            />
          </Grid>
        </Grid>
        <Box sx={{ width: 260, mt: 2 }}>
          <ImageWidget
            label="Campaign banner (optional)"
            value={presentation.bannerImageUrl ?? ""}
            onChange={(value: string) => onChange({ bannerImageUrl: value || null })}
          />
        </Box>
      </CardContent>
    </Card>

    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          Appearance
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              label="Wheel size"
              value={presentation.wheelSize}
              onChange={(event) => onChange({ wheelSize: event.target.value as PresentationFormData["wheelSize"] })}
              fullWidth
            >
              <MenuItem value="compact">Compact</MenuItem>
              <MenuItem value="standard">Standard</MenuItem>
              <MenuItem value="large">Large</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              label="Celebration intensity"
              value={presentation.celebrationIntensity}
              onChange={(event) =>
                onChange({ celebrationIntensity: event.target.value as PresentationFormData["celebrationIntensity"] })
              }
              fullWidth
            >
              <MenuItem value="subtle">Subtle</MenuItem>
              <MenuItem value="normal">Normal</MenuItem>
              <MenuItem value="festive">Festive</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Accent color"
              type="color"
              value={presentation.accentColor}
              onChange={(event) => onChange({ accentColor: event.target.value })}
              fullWidth
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>

    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          Floating launcher button
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Shown as a floating button on the Home screen that opens the wheel. Upload an image or
          animated GIF to replace the default gift-box icon.
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ width: 140 }}>
              <ImageWidget
                label="Launcher icon (optional)"
                value={presentation.fabIconUrl ?? ""}
                onChange={(value: string) => onChange({ fabIconUrl: value || null })}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Stack spacing={1}>
              <TextField
                label="Background color"
                type="color"
                value={presentation.fabBackgroundColor ?? DEFAULT_FAB_BACKGROUND_COLOR}
                onChange={(event) => onChange({ fabBackgroundColor: event.target.value })}
                fullWidth
              />
              {presentation.fabBackgroundColor && (
                <Button size="small" sx={{ alignSelf: "flex-start" }} onClick={() => onChange({ fabBackgroundColor: null })}>
                  Reset to default
                </Button>
              )}
            </Stack>
          </Grid>
        </Grid>
      </CardContent>
    </Card>

    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          Spin button
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={5}>
            <TextField
              label="Button text"
              value={presentation.spinButtonText}
              onChange={(event) => onChange({ spinButtonText: event.target.value })}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={7}>
            <TextField
              select
              label="Placement"
              value={presentation.spinButtonPlacement}
              onChange={(event) =>
                onChange({ spinButtonPlacement: event.target.value as PresentationFormData["spinButtonPlacement"] })
              }
              fullWidth
            >
              <MenuItem value="centerCircle">Circle in the center of the wheel</MenuItem>
              <MenuItem value="topButton">Button above the wheel</MenuItem>
              <MenuItem value="bottomButton">Button below the wheel</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </CardContent>
    </Card>

    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          What users see
        </Typography>
        <Stack>
          <FormControlLabel
            control={
              <Switch
                checked={presentation.soundEnabledByDefault}
                onChange={(event) => onChange({ soundEnabledByDefault: event.target.checked })}
              />
            }
            label="Sound enabled by default"
          />
          <FormControlLabel
            control={
              <Switch checked={presentation.showPrizePool} onChange={(event) => onChange({ showPrizePool: event.target.checked })} />
            }
            label="Show today's prize pool"
          />
          <FormControlLabel
            control={<Switch checked={presentation.showOdds} onChange={(event) => onChange({ showOdds: event.target.checked })} />}
            label="Show prize odds to users"
          />
          <FormControlLabel
            control={
              <Switch
                checked={presentation.showRemainingSpins}
                onChange={(event) => onChange({ showRemainingSpins: event.target.checked })}
              />
            }
            label="Show remaining spins today"
          />
        </Stack>
      </CardContent>
    </Card>
  </Stack>
)

export default WheelPresentationConfig
